import React from 'react';
import { Experience } from '../../../../../types/resume';
import { Building2, MapPin, Calendar, Trash2 } from 'lucide-react';
import Input from '../../../../ui/Input';
import RichTextEditor from '../../../../ui/RichTextEditor';
import AchievementList from './AchievementList';

interface Props {
  experience: Experience;
  index: number;
  onUpdate: (id: string, updates: Partial<Experience>) => void;
  onRemove: (id: string) => void;
}

export default function ExperienceItem({ experience, index, onUpdate, onRemove }: Props) {
  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Experience {index + 1}</h3>
        <button
          onClick={() => onRemove(experience.id)}
          className="text-red-500 hover:text-red-600"
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Building2 className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
            <Input
              label="Company"
              value={experience.company}
              onChange={(e) => onUpdate(experience.id, { company: e.target.value })}
              placeholder="Company Name"
              className="pl-10"
              required
            />
          </div>
          <div className="relative">
            <MapPin className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
            <Input
              label="Location"
              value={experience.location}
              onChange={(e) => onUpdate(experience.id, { location: e.target.value })}
              placeholder="City, Country"
              className="pl-10"
            />
          </div>
        </div>

        <Input
          label="Position"
          value={experience.position}
          onChange={(e) => onUpdate(experience.id, { position: e.target.value })}
          placeholder="Job Title"
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Calendar className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
            <Input
              type="month"
              label="Start Date"
              value={experience.startDate}
              onChange={(e) => onUpdate(experience.id, { startDate: e.target.value })}
              className="pl-10"
              required
            />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
            <div>
              <Input
                type="month"
                label="End Date"
                value={experience.endDate}
                onChange={(e) => onUpdate(experience.id, { endDate: e.target.value })}
                className="pl-10"
                disabled={experience.current}
                required={!experience.current}
              />
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id={`current-${experience.id}`}
                  checked={experience.current}
                  onChange={(e) => onUpdate(experience.id, { current: e.target.checked })}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <label htmlFor={`current-${experience.id}`} className="text-sm text-gray-600">
                  I currently work here
                </label>
              </div>
            </div>
          </div>
        </div>

        <RichTextEditor
          label="Description"
          value={experience.description}
          onChange={(value) => onUpdate(experience.id, { description: value })}
          placeholder="Describe your responsibilities and achievements..."
          rows={4}
          required
        />

        <AchievementList
          achievements={experience.achievements}
          onUpdate={(achievements) => onUpdate(experience.id, { achievements })}
        />
      </div>
    </div>
  );
}